import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { formatDistanceToNow, format } from 'date-fns';
import {
  ArrowUp,
  User,
  Building2,
  Calendar,
  Tag,
  MessageSquare,
  Send,
  Loader2,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { StandardSheet } from '@/components/shared/StandardSheet';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useFormProtection } from '@/hooks/useFormProtection';
import {
  StandardProposal,
  ProposalComment,
  useProposalComments,
  useHasVoted,
  useVoteProposal,
  useAddComment,
} from '@/hooks/useProposals';
import { useAdfElements, AdfElement } from '@/hooks/useAdfStandard';
import { toast } from 'sonner';
import { PROPOSAL_TYPE_LABELS, PROPOSAL_TYPE_COLORS } from '@/lib/admin/constants';

interface ProposalDetailProps {
  proposal: StandardProposal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusLabels: Record<string, string> = {
  pending: 'Pending Review',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  implemented: 'Implemented',
};

const statusBadgeColors: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
  under_review: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  approved: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  implemented: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
};

const commentSchema = z.object({
  content: z.string().min(1, 'Comment cannot be empty').max(2000),
  commenter_name: z.string().min(2, 'Name is required').max(100),
  commenter_email: z.string().email('Valid email is required'),
});

type CommentFormData = z.infer<typeof commentSchema>;

// Helper component to display linked ADF element
function AdfElementLink({ elementId }: { elementId: string }) {
  const { data: elements = [] } = useAdfElements();
  const element = elements.find(e => e.id === elementId);
  
  if (!element) {
    return <span className="text-muted-foreground">Unknown element</span>;
  }

  return (
    <Link 
      to={`/adf-standard?element=${element.slug}`}
      className="inline-flex items-center gap-1.5 text-primary hover:underline"
    >
      <code className="bg-muted px-1 rounded text-xs">&lt;{element.tag_name}&gt;</code>
      <span>{element.display_name}</span>
      <ExternalLink className="h-3 w-3" />
    </Link>
  );
}

export function ProposalDetail({ proposal, open, onOpenChange }: ProposalDetailProps) {
  const { user, profile } = useAuth();
  const hasVoted = useHasVoted(proposal?.id || '');
  const voteMutation = useVoteProposal();
  const addCommentMutation = useAddComment();
  const { data: comments = [], isLoading: loadingComments } = useProposalComments(proposal?.id || '');
  const { honeypotProps, validateSubmission, recordSubmission, isRateLimited } = useFormProtection('comment');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      commenter_name: profile?.full_name || '',
      commenter_email: user?.email || '',
    },
  });

  const handleVote = () => {
    if (!proposal) return;
    voteMutation.mutate({
      proposalId: proposal.id,
      action: hasVoted ? 'unvote' : 'vote',
    });
  };

  const onSubmitComment = async (data: CommentFormData) => {
    if (!proposal) return;
    
    // Check honeypot and rate limiting
    const protection = validateSubmission();
    if (!protection.valid) {
      toast.error(protection.error || 'Comment failed. Please try again.');
      return;
    }

    try {
      await addCommentMutation.mutateAsync({
        proposal_id: proposal.id,
        content: data.content,
        commenter_name: data.commenter_name,
        commenter_email: data.commenter_email,
      });
      recordSubmission();
      reset({ ...data, content: '' });
    } catch (error) {
      toast.error('Failed to post comment. Please try again.');
    }
  };

  if (!proposal) return null;

  const isAdfType = ['adf_element', 'adf_mapping', 'adf_attribute'].includes(proposal.proposal_type);

  const footerContent = (
    <div className="flex items-center justify-between">
      <Button
        variant={hasVoted ? 'default' : 'outline'}
        size="sm"
        className="gap-2"
        onClick={handleVote}
        disabled={voteMutation.isPending}
      >
        <ArrowUp className={cn('h-4 w-4', hasVoted && 'fill-current')} />
        <span className="font-semibold">{proposal.vote_count}</span>
        <span className="text-muted-foreground">votes</span>
      </Button>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <MessageSquare className="h-4 w-4" />
        {comments.length} comments
      </div>
    </div>
  );

  return (
    <StandardSheet
      open={open}
      onOpenChange={onOpenChange}
      title={proposal.title}
      size="md"
      footer={footerContent}
    >
      {/* Badges */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Badge variant="outline" className={PROPOSAL_TYPE_COLORS[proposal.proposal_type] || ''}>
          {PROPOSAL_TYPE_LABELS[proposal.proposal_type] || proposal.proposal_type}
        </Badge>
        <Badge variant="outline" className={statusBadgeColors[proposal.status]}>
          {statusLabels[proposal.status]}
        </Badge>
      </div>

      {/* Meta Info */}
      <div className="space-y-2 text-sm text-muted-foreground mb-6">
        <div className="flex items-center gap-1.5">
          <User className="h-3.5 w-3.5" />
          <span>{proposal.submitter_name}</span>
        </div>
        {proposal.submitter_organization && (
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5" />
            <span>{proposal.submitter_organization}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          <span>{format(new Date(proposal.created_at), 'MMM d, yyyy')}</span>
        </div>
        {proposal.target_category && (
          <div className="flex items-center gap-1.5">
            <Tag className="h-3.5 w-3.5" />
            <span>{proposal.target_category}</span>
          </div>
        )}
      </div>

      <Separator className="mb-6" />

      {/* Description */}
      <div className="mb-6">
        <h4 className="font-medium mb-2">Description</h4>
        <p className="text-muted-foreground whitespace-pre-wrap">{proposal.description}</p>
      </div>

      {/* Justification */}
      <div className="mb-6">
        <h4 className="font-medium mb-2">Justification</h4>
        <p className="text-muted-foreground whitespace-pre-wrap">{proposal.justification}</p>
      </div>

      {/* Field-specific details */}
      {proposal.proposal_type === 'field' && proposal.proposed_field_name && (
        <div className="bg-muted/50 rounded-lg p-4 space-y-2 mb-6">
          <h4 className="font-medium">Proposed Field</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>{' '}
              <code className="bg-background px-1 rounded">{proposal.proposed_field_name}</code>
            </div>
            {proposal.proposed_field_type && (
              <div>
                <span className="text-muted-foreground">Type:</span>{' '}
                <code className="bg-background px-1 rounded">{proposal.proposed_field_type}</code>
              </div>
            )}
            {proposal.proposed_field_format && (
              <div>
                <span className="text-muted-foreground">Format:</span>{' '}
                <code className="bg-background px-1 rounded">{proposal.proposed_field_format}</code>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Required:</span>{' '}
              {proposal.proposed_field_required ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      )}

      {/* Enum-specific details */}
      {proposal.proposal_type === 'enum_value' && proposal.proposed_enum_value && (
        <div className="bg-muted/50 rounded-lg p-4 space-y-2 mb-6">
          <h4 className="font-medium">Proposed Enum Value</h4>
          <div className="text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Value:</span>{' '}
              <code className="bg-background px-1 rounded">{proposal.proposed_enum_value}</code>
            </div>
            {proposal.target_field_name && (
              <div>
                <span className="text-muted-foreground">For field:</span>{' '}
                <code className="bg-background px-1 rounded">{proposal.target_field_name}</code>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADF Element-specific details */}
      {proposal.proposal_type === 'adf_element' && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 space-y-2 mb-6">
          <h4 className="font-medium text-amber-600 dark:text-amber-400">Proposed ADF Element</h4>
          <div className="text-sm space-y-2">
            {proposal.proposed_field_name && (
              <div>
                <span className="text-muted-foreground">Tag Name:</span>{' '}
                <code className="bg-background px-1.5 py-0.5 rounded">&lt;{proposal.proposed_field_name}&gt;</code>
              </div>
            )}
            {proposal.target_adf_element_id && (
              <div>
                <span className="text-muted-foreground">Parent Element:</span>{' '}
                <AdfElementLink elementId={proposal.target_adf_element_id} />
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Required:</span>{' '}
              {proposal.proposed_field_required ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      )}

      {/* ADF Mapping-specific details */}
      {proposal.proposal_type === 'adf_mapping' && (
        <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 space-y-2 mb-6">
          <h4 className="font-medium text-cyan-600 dark:text-cyan-400">Proposed ADF Mapping</h4>
          <div className="text-sm space-y-2">
            {proposal.proposed_field_name && (
              <div>
                <span className="text-muted-foreground">Field Name:</span>{' '}
                <code className="bg-background px-1.5 py-0.5 rounded">{proposal.proposed_field_name}</code>
              </div>
            )}
            {proposal.target_adf_element_id && (
              <div>
                <span className="text-muted-foreground">Target Element:</span>{' '}
                <AdfElementLink elementId={proposal.target_adf_element_id} />
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Required:</span>{' '}
              {proposal.proposed_field_required ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      )}

      {/* ADF Attribute-specific details */}
      {proposal.proposal_type === 'adf_attribute' && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4 space-y-2 mb-6">
          <h4 className="font-medium text-rose-600 dark:text-rose-400">Proposed ADF Attribute</h4>
          <div className="text-sm space-y-2">
            {proposal.proposed_field_name && (
              <div>
                <span className="text-muted-foreground">Attribute Name:</span>{' '}
                <code className="bg-background px-1.5 py-0.5 rounded">{proposal.proposed_field_name}</code>
              </div>
            )}
            {proposal.target_adf_element_id && (
              <div>
                <span className="text-muted-foreground">Target Element:</span>{' '}
                <AdfElementLink elementId={proposal.target_adf_element_id} />
              </div>
            )}
            {proposal.proposed_enum_value && (
              <div>
                <span className="text-muted-foreground">Valid Values:</span>{' '}
                <span className="text-foreground">{proposal.proposed_enum_value}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Notes */}
      {proposal.admin_notes && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-yellow-600 dark:text-yellow-400 mb-2">
            Admin Response
          </h4>
          <p className="text-sm whitespace-pre-wrap">{proposal.admin_notes}</p>
          {proposal.reviewed_at && (
            <p className="text-xs text-muted-foreground mt-2">
              Reviewed {formatDistanceToNow(new Date(proposal.reviewed_at), { addSuffix: true })}
            </p>
          )}
        </div>
      )}

      <Separator className="mb-6" />

      {/* Comments Section */}
      <div>
        <h4 className="font-medium mb-4 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Comments ({comments.length})
        </h4>

        {loadingComments ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>
        )}

        {/* Add Comment Form */}
        <form onSubmit={handleSubmit(onSubmitComment)} className="mt-6 space-y-4">
          {/* Honeypot field - hidden from humans, catches bots */}
          <input type="text" {...honeypotProps} />
          
          {/* Rate limit warning */}
          {isRateLimited && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              Too many comments. Please wait a moment before trying again.
            </div>
          )}

          <Textarea
            placeholder="Add a comment..."
            rows={3}
            {...register('content')}
          />
          {errors.content && (
            <p className="text-sm text-destructive">{errors.content.message}</p>
          )}

          {!user && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="commenter_name" className="text-xs">Name *</Label>
                <Input
                  id="commenter_name"
                  placeholder="Your name"
                  {...register('commenter_name')}
                />
                {errors.commenter_name && (
                  <p className="text-xs text-destructive">{errors.commenter_name.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="commenter_email" className="text-xs">Email *</Label>
                <Input
                  id="commenter_email"
                  type="email"
                  placeholder="your@email.com"
                  {...register('commenter_email')}
                />
                {errors.commenter_email && (
                  <p className="text-xs text-destructive">{errors.commenter_email.message}</p>
                )}
              </div>
            </div>
          )}

          <Button
            type="submit"
            size="sm"
            disabled={addCommentMutation.isPending || isRateLimited}
          >
            {addCommentMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Post Comment
          </Button>
        </form>
      </div>
    </StandardSheet>
  );
}

function CommentItem({ comment }: { comment: ProposalComment }) {
  const initials = comment.commenter_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex gap-3">
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{comment.commenter_name}</span>
          {comment.is_admin && (
            <Badge variant="outline" className="text-xs py-0 h-5 gap-1">
              <Shield className="h-3 w-3" />
              Admin
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
          {comment.content}
        </p>
      </div>
    </div>
  );
}